import type * as React from "react";
import { useCallback } from "react";
import FilterGroupItem from "../../FilterGroupItem";
import SingleTechFilter from "./SingleTechFilter";

import { useTranslation } from "react-i18next";
import { Box, Button } from "~/common/mui";
import { verticalStacker } from "~/styles/styledMixins";
import type { TechValueFilterCategoryData } from "~/features/search/models/filterConfigModels";
import { useFormContext, useWatch } from "react-hook-form";
import { useTechFilterReset } from "../../TechFilterResetContext";

interface Props {
  data: TechValueFilterCategoryData;
  hidden?: boolean;
  hideTechFilterButtons?: boolean;
}

const ConnectedSingleTechFilter = ({
  uid,
  title,
  placeholderSuffix,
  dataTestId,
}: {
  uid: string;
  title: React.ReactNode;
  placeholderSuffix?: string;
  dataTestId?: string;
}) => {
  const { control, setValue } = useFormContext();
  const minName = `${uid}.min`;
  const maxName = `${uid}.max`;

  const min = useWatch({ control, name: minName });
  const max = useWatch({ control, name: maxName });

  const onUpdateValues = useCallback(
    (type: "min" | "max", value: number | undefined) => {
      setValue(`${uid}.${type}`, value, { shouldDirty: true, shouldTouch: true });
    },
    [uid, setValue],
  );

  return (
    <SingleTechFilter
      title={title}
      min={min}
      max={max}
      onUpdateValues={onUpdateValues}
      placeholderSuffix={placeholderSuffix}
      data-test-id={dataTestId}
    />
  );
};

export const InnerTechFilterCategory = (props: Props) => {
  const { items } = props.data;
  const { t } = useTranslation();

  const remodeledItems = items.map((obj) => {
    const titleTranslated = (
      <span dangerouslySetInnerHTML={{ __html: t(obj.title) + ` ${obj.abbreviation ?? ""} ${obj.unit ?? ""}` }} />
    );
    return { ...obj, title: titleTranslated };
  });

  return remodeledItems.map((item) => (
    <ConnectedSingleTechFilter key={item.uid} uid={item.uid} title={item.title} dataTestId={item.uid} />
  ));
};

export const TechFilterCategory = (props: Props) => {
  const { control, formState } = useFormContext();
  const { items } = props.data;
  const { resetFilters } = useTechFilterReset();

  const onClearClick = () => {
    const filterUids = items.map((item) => item.uid);
    resetFilters(filterUids);
  };

  // Watch all values for this category to determine if clear is enabled and if it should be expanded
  const allValues = useWatch({ control });

  const isClearEnabled = items.some(
    (item) => allValues[`${item.uid}`]?.["min"] !== undefined || allValues[`${item.uid}`]?.["max"] !== undefined,
  );

  const { title, initiallyExpanded } = props.data;
  const { t } = useTranslation();

  const toExpand = initiallyExpanded || isClearEnabled;

  return (
    <FilterGroupItem
      title={title}
      initiallyExpanded={toExpand}
      data-test-id={props.data.facetId ?? undefined}
      hidden={props.hidden}
    >
      <Box sx={{ ...verticalStacker(), gap: "32px" }}>
        <InnerTechFilterCategory data={props.data} hidden={props.hidden} />
        {!props.hideTechFilterButtons && (
          <Box sx={{ display: "flex", gap: "16px", justifyContent: "flex-end" }}>
            <Button
              variant={"text"}
              type={"button"}
              disabled={!isClearEnabled}
              onClick={onClearClick}
              data-test-id={`tech-filter-category-${props.data.facetId}-reset`}
            >
              {t("searchPage.refinement.button.clear")}
            </Button>
            <Button
              type={"submit"}
              disabled={!formState.isDirty}
              data-test-id={`tech-filter-category-${props.data.facetId}-apply`}
            >
              {t("searchPage.refinement.button.apply")}
            </Button>
          </Box>
        )}
      </Box>
    </FilterGroupItem>
  );
};
